import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import enTranslations from '@myosh/odin-translations/translations/en.json';
import LanguageDetector from 'i18next-browser-languagedetector';

type TranslationFile = {
  default: Record<string, string>;
};

export const supportedLanguages = [
  'ar',
  'bn',
  'de',
  'en',
  'es',
  'fi',
  'fr',
  'hi',
  'hr',
  'it',
  'ja',
  'ko',
  'pa',
  'pt-PT',
  'pt',
  'ru',
  'sq',
  'tl',
  'zh-TW',
  'zh',
];

// Matches the API provided locale code to the supported languages, falling back to the closest match or 'en'
export const matchApiLanguageToTranslationFile = (localeCode: string): string => {
  if (!localeCode.includes('-')) {
    return supportedLanguages.includes(localeCode) ? localeCode : 'en';
  } else {
    if (supportedLanguages.includes(localeCode)) {
      return localeCode;
    } else {
      const base = localeCode.split('-')[0];
      return supportedLanguages.includes(base) ? base : 'en';
    }
  }
};

const loaders: Record<string, () => Promise<TranslationFile>> = {
  ar: () => import('@myosh/odin-translations/translations/ar.json'),
  bn: () => import('@myosh/odin-translations/translations/bn.json'),
  de: () => import('@myosh/odin-translations/translations/de.json'),
  en: () => import('@myosh/odin-translations/translations/en.json'),
  es: () => import('@myosh/odin-translations/translations/es.json'),
  fi: () => import('@myosh/odin-translations/translations/fi.json'),
  fr: () => import('@myosh/odin-translations/translations/fr.json'),
  hi: () => import('@myosh/odin-translations/translations/hi.json'),
  hr: () => import('@myosh/odin-translations/translations/hr.json'),
  it: () => import('@myosh/odin-translations/translations/it.json'),
  ja: () => import('@myosh/odin-translations/translations/ja.json'),
  ko: () => import('@myosh/odin-translations/translations/ko.json'),
  pa: () => import('@myosh/odin-translations/translations/pa.json'),
  'pt-PT': () => import('@myosh/odin-translations/translations/pt-PT.json'),
  pt: () => import('@myosh/odin-translations/translations/pt.json'),
  ru: () => import('@myosh/odin-translations/translations/ru.json'),
  sq: () => import('@myosh/odin-translations/translations/sq.json'),
  tl: () => import('@myosh/odin-translations/translations/tl.json'),
  'zh-TW': () => import('@myosh/odin-translations/translations/zh-TW.json'),
  zh: () => import('@myosh/odin-translations/translations/zh.json'),
};

export const loadTranslation = async (language: string) => {
  const loader = loaders[language] || loaders['en'];
  try {
    const module = await loader();
    return module.default;
  } catch (err) {
    console.error(`Failed to load translation for "${language}":`, err);
    return {};
  }
};

export const resources = {
  en: {
    translation: enTranslations,
  },
};

i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: localStorage.getItem('vikingUserLocale') || 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    debug: false,
    react: { useSuspense: false },
    resources: resources,
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'vikingUserLocale',
      caches: ['localStorage'],
      excludeCacheFor: ['cimode'],
    },
  });

// Handle language changes and loads translation file for the new language
i18next.on('languageChanged', async (lng) => {
  const translations = await loadTranslation(lng);
  i18next.addResourceBundle(lng, 'translation', translations, true, true);
});

export const setUserLocale = (localeCode: string) => {
  const language = matchApiLanguageToTranslationFile(localeCode);
  localStorage.setItem('vikingUserLocale', language);
  i18next.changeLanguage(language);
};

export default i18next;
